import { Component, effect, inject, OnInit } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SharedModule } from 'primeng/api';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { AppUtilityService } from '../../../utility/app.utility';
import { MultiSelectModule } from 'primeng/multiselect';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { UserDropdownComponent } from '../../atoms/user-dropdown/user-dropdown.component';
import { getState } from '@ngrx/signals';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';

@Component({
  selector: 'app-bookmark-category-form',
  standalone: true,
  imports: [
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    MultiSelectModule,
    WorkspaceDropdownComponent,
    UserDropdownComponent,
    FormFooterComponent,
  ],
  templateUrl: './bookmark-category-form.component.html',
  styleUrl: './bookmark-category-form.component.scss',
})
export class BookmarkCategoryFormComponent
  extends BaseFormComponent<BookmarkCategory>
  implements OnInit
{
  readonly store = inject(BookmarkCategoryStore);
  readonly activeUserStore = inject(ActiveUserStore);

  override form: FormGroup = this.fb.group({});
  override itemToEdit?: BookmarkCategory;
  override formInitialized = false;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form with predefined values from the item to edit, user store,
   * and other properties. Configures the reactive form structure with predefined
   * validators and default values.
   *
   * @return {void} Does not return a value. Sets up the form and initializes it within the component.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();

    this.form = this.fb.group({
      workspaces: [this.itemToEdit?.workspaces || ''],
      title: [this.itemToEdit?.title || '', Validators.required],
      private: [this.itemToEdit?.private || false],
      sorting: [this.itemToEdit?.sorting || this.store.count()],
      privateUsers: [
        this.itemToEdit?.privateUsers || [this.activeUserStore.user()?._id],
      ],
    });

    this.formInitialized = true;
  }

  /**
   * Handles the submission process by invoking the submit method.
   *
   * @return {void} Does not return any value.
   */
  onSubmit(): void {
    this.submit();
  }
}
